import { Tonality } from '@dsny/dsny-component-library';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CUSTOMER_SUPPORT_EMAIL } from 'src/utils/Urls';
import { AppDispatch, RootState } from 'src/app/store';
import { useDispatch, useSelector } from 'react-redux';
import { validateSpinsDetection } from 'src/utils/VerifyTrackStatus';
import {
  MetricWrapper,
  TotalSpinsStyle,
  SongTitleStyleMobile,
  ArtistStyleMobile,
  HeaderStyleMobile,
  ContactLink,
  SomethingWrongWrapper,
  HeaderButtonMobile,
  HeaderSeparatorMobile,
  ClickableWrapper,
  NoResultFoundWrapper,
  BulkSelectionWrapperStyles,
  SingleSelectionWrapperStyles,
  TableFlexWrapper,
  TableMessagesWrapper,
  TableContainer,
} from './SongTable.styles';
import { saveTableStatusMobile, SongLibrary } from '../Library.slice';
import { useSorting } from './useSorting';
import ContextDropdown from '../ContextDropdown/ContextDropdown';
import { SortingRule, TableSongData } from '../Library';

interface SongTableMobileProps {
  songsLibrary: SongLibrary[];
  rowSelection: { [key: number]: boolean };
  tableData: TableSongData[];
  sortedTableData: TableSongData[];
  isFetching: boolean;
  getComparisonBadge: (spinsDifference: number) => React.ReactNode;
  getTrackStatus: (
    startTrackingOn: string,
    endTrackingOn: string,
    songId: string
  ) => React.ReactNode;
  handleNavigate: (songId: string) => void;
  setRowSelection: (
    selection:
      | Record<number, boolean>
      | ((selection: Record<number, boolean>) => Record<number, boolean>)
  ) => void;
  exportCSV: (songIds: string[]) => void;
  handleSorting: (sorting: SortingRule[], songSortSelected: boolean) => void;
}

// Table row type definition
type TableRow = {
  songId: string;
  song: string;
  artist: string;
  spins_all_count: number;
  spins_count: number;
  spins_difference: number;
  status: number;
  startTrackingOn: string;
  endTrackingOn: string;
};

const TABLE_SKELETON_ROW_COUNT = 7;

// Table component - mobile
const SongTableMobile: React.FC<SongTableMobileProps> = ({
  rowSelection,
  tableData,
  sortedTableData,
  isFetching,
  getComparisonBadge,
  handleNavigate,
  setRowSelection,
  exportCSV,
  handleSorting,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const { tableStatus } = useSelector((state: RootState) => state.library);

  // Sorting hook
  const { sorting, onSortingChange } = useSorting(
    tableStatus.sortingId,
    tableStatus.sortingOrder
  );

  // State to manage song/artist sort selection - mobile
  const [songSortSelected, setSongSortSelected] = useState<boolean>(
    tableStatus.songSortSelected
  );

  // Handling sort selection - mobile
  const handleSongSelection = (isSong: boolean) => {
    setSongSortSelected(isSong);
    dispatch(saveTableStatusMobile(isSong));
  };

  useEffect(() => {
    handleSorting(sorting, songSortSelected);
  }, [sorting, tableData]);

  // Table columns definition
  const cols: Tonality.ColumnDef<TableRow>[] = [
    {
      id: 'select-col',
      enableSorting: false,
      size: 48,
      maxSize: 48,
      isSelectionColumn: true,
      header: (state: Tonality.HeaderRender, onChange) => {
        const { someRowsAreSelected, allRowsAreSelected } = state;

        return (
          // Bulk selection
          <Tonality.Checkbox
            checked={someRowsAreSelected ? 'mixed' : allRowsAreSelected}
            onChange={onChange() as never}
            wrapperStyle={{ ...BulkSelectionWrapperStyles }}
          />
        );
      },
      render: ({ row }) => (
        // Single selection
        <Tonality.Checkbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
          wrapperStyle={{ ...SingleSelectionWrapperStyles }}
        />
      ),
    },
    {
      id: 'song_artist',
      accessorKey: 'song_artist',
      enableSorting: true,
      enableResizing: true,
      size: 168,
      minSize: 144,
      maxSize: 190,
      header: () => (
        <HeaderStyleMobile>
          {/* Header song button */}
          <HeaderButtonMobile
            onClick={() => handleSongSelection(true)}
            isMobileSortActive={songSortSelected}
          >
            {t('LIBRARY_TABLE_HEADER_SONG_MOBILE')}
          </HeaderButtonMobile>
          <HeaderSeparatorMobile>/</HeaderSeparatorMobile>
          {/* Header artist button */}
          <HeaderButtonMobile
            onClick={() => handleSongSelection(false)}
            isMobileSortActive={!songSortSelected}
          >
            {t('LIBRARY_TABLE_HEADER_ARTIST_MOBILE')}
          </HeaderButtonMobile>
        </HeaderStyleMobile>
      ),
      render: (cell, row) => (
        <ClickableWrapper onClick={() => handleNavigate(row.original.songId)}>
          <SongTitleStyleMobile>{row.original.song}</SongTitleStyleMobile>
          <ArtistStyleMobile>{row.original.artist}</ArtistStyleMobile>
        </ClickableWrapper>
      ),
    },
    {
      id: 'spins',
      accessorKey: 'spins',
      enableSorting: true,
      enableResizing: true,
      size: 104,
      minSize: 88,
      maxSize: 120,
      tooltip: t('LIBRARY_TABLE_HEADER_SPINS_TOOLTIP'),
      header: () => (
        <HeaderStyleMobile>{t('LIBRARY_TABLE_HEADER_SPINS')}</HeaderStyleMobile>
      ),
      render: (cell, row) => (
        <MetricWrapper onClick={() => handleNavigate(row.original.songId)}>
          <TotalSpinsStyle>{row.original.spins_all_count}</TotalSpinsStyle>
          {getComparisonBadge(Number(row.original.spins_difference))}
        </MetricWrapper>
      ),
    },
    {
      id: 'context_menu',
      enableSorting: false,
      size: 40,
      maxSize: 40,
      render: (cell, row) => (
        <ContextDropdown
          songId={row.original.songId}
          displayMenuOnTop={sortedTableData.length === row.index + 1}
          isExportAvailable={validateSpinsDetection(
            row.original.spins_all_count
          )}
          handleNavigate={handleNavigate}
          exportCSV={exportCSV}
        />
      ),
    },
  ];

  return (
    <TableFlexWrapper>
      <TableMessagesWrapper>
        {/* Something went wrong message */}
        {tableData.length === 0 && !isFetching && (
          <SomethingWrongWrapper>
            <span>
              {t('LIBRARY_TABLE_ERROR_MESSAGE')}{' '}
              <ContactLink
                href={`mailto:${CUSTOMER_SUPPORT_EMAIL}`}
                target="_blank"
                rel="noreferrer"
              >
                {t('LIBRARY_TABLE_CONTACT_SUPPORT_LINK')}
              </ContactLink>
            </span>
          </SomethingWrongWrapper>
        )}

        {/* No results found message */}
        {!sortedTableData.length && !isFetching && (
          <NoResultFoundWrapper>
            <span>{t('LIBRARY_TABLE_NO_RESULT_FOUND_MESSAGE')}</span>
          </NoResultFoundWrapper>
        )}
      </TableMessagesWrapper>
      <TableContainer>
        <Tonality.Table
          data={sortedTableData}
          cols={cols}
          mode="light"
          setting="mtr"
          showPagination={false}
          pagination={{
            pageIndex: 0,
            pageSize: TABLE_SKELETON_ROW_COUNT,
            rowCount: sortedTableData.length,
          }}
          sorting={sorting}
          loading={isFetching}
          rowSelection={rowSelection}
          onSortingChange={onSortingChange}
          onRowSelectionChange={setRowSelection}
        />
      </TableContainer>
    </TableFlexWrapper>
  );
};

export default SongTableMobile;
